import getConfig from 'next/config'
import { useEffect, useRef } from 'react'

import { UseTurnstileParams } from './type'

export function useTurnstile({
  onSuccess,
  onError,
  onExpire,
}: UseTurnstileParams) {
  const { publicRuntimeConfig } = getConfig()
  const siteKey: string = publicRuntimeConfig.TURNSTILE_SITE_KEY
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  // Stable refs so the Cloudflare widget always invokes the latest handlers
  // even if the parent re-renders and passes new function references.
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  const onExpireRef = useRef(onExpire)
  onSuccessRef.current = onSuccess
  onErrorRef.current = onError
  onExpireRef.current = onExpire

  // Dispose the Cloudflare widget on unmount (remove() is the documented
  // teardown; it clears the widget's internal timers/observers).
  useEffect(() => {
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [])

  // Called from the Cloudflare script's onReady (also fires on every mount).
  // Idempotent so StrictMode's double-mount / re-mounts never stack widgets.
  function renderWidget() {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) {
      return
    }
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token: string) => onSuccessRef.current(token),
      'error-callback': () => onErrorRef.current?.(),
      'expired-callback': () => onExpireRef.current?.(),
      // The signin page is light-only; the default 'auto' would follow the
      // OS color scheme and show a dark widget on a light page.
      theme: 'light',
    })
  }

  function reset() {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
    }
  }

  return { containerRef, renderWidget, reset }
}
